import React, { useState } from "react";
import {
    Card,
    Table,
    Paper,
    Popover,
    TableRow,
    MenuItem,
    TableBody,
    TableCell,
    Typography,
    IconButton,
    TableContainer,
    TableHead,
    CircularProgress,
    Box,
    Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Iconify from "./iconify";
import Scrollbar from "./scrollbar";
import { getUserPermissions } from "src/utils/permissionsUtils";
import { fDateTime } from "src/utils/formatTime";
import { fCoins } from "src/utils/formatNumber";

const StagesTable = ({
    rows,
    loading,
    stagePeriods,
    onEditStage,
    onViewLeaderboard,
}) => {
    const navigate = useNavigate();
    const [open, setOpen] = useState(null);
    const [openId, setOpenId] = useState(null);
    const { isViewer } = getUserPermissions();

    const handleOpenMenu = (event) => {
        setOpen(event.currentTarget);
        setOpenId(event.currentTarget.id);
    };

    const handleCloseMenu = () => {
        setOpen(null);
        setOpenId(null);
    };

    const handleMenuAction = (action, stageId) => {
        handleCloseMenu();

        switch (action) {
            case "edit":
                onEditStage && onEditStage(stageId);
                break;
            case "leaderboard":
                onViewLeaderboard ? onViewLeaderboard(stageId) : navigate(`/dashboard/stage/leaderboard?stage_id=${stageId}`);
                break;
            default:
                break;
        }
    };

    const getStageStatus = (row) => {
        const now = new Date();
        const start = new Date(row.startDate);
        const end = new Date(row.endDate);

        if (now >= start && now <= end) {
            return (
                <Chip
                    label="Live"
                    sx={{
                        backgroundColor: "#00BC6D",
                        color: "#fff",
                    }}
                />
            );
        }

        if (now < start) {
            return (
                <Chip
                    label="Upcoming"
                    sx={{
                        backgroundColor: "#FF8423",
                        color: "#fff",
                    }}
                />
            );
        }

        if (now > end) {
            return (
                <Chip
                    label="Completed"
                    sx={{
                        backgroundColor: "#AAAAAA",
                        color: "#fff",
                    }}
                />
            );
        }

        return "";
    };

    return (
        <>
            <Card>
                {(rows === null || loading || (stagePeriods === null)) ? (
                    <Box
                        sx={{ display: "flex", justifyContent: "center" }}
                        style={{ padding: 20 }}
                    >
                        <CircularProgress />
                    </Box>
                ) : rows.length === 0 || stagePeriods.length === 1 ? (
                    <Box
                        sx={{ display: "flex", justifyContent: "center" }}
                        style={{ padding: 20 }}
                    >
                        <Typography variant="body1" color="text.secondary">
                            No stages found for the selected period.
                        </Typography>
                    </Box>
                ) : (
                    <Scrollbar>
                        <TableContainer component={Paper}>
                            <Table sx={{ minWidth: 650 }} aria-label="stages table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Stage ID</TableCell>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Start Date</TableCell>
                                        <TableCell>End Date</TableCell>
                                        <TableCell>Games</TableCell>
                                        <TableCell>Lineups</TableCell>
                                        <TableCell>Completed</TableCell>
                                        <TableCell>Paid</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="right"></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map((row, index) => (
                                        <TableRow
                                            key={index}
                                            sx={{
                                                "&:last-child td, &:last-child th": { border: 0 },
                                            }}
                                        >
                                            <TableCell component="th" scope="row">
                                                {row.stageId}
                                            </TableCell>
                                            <TableCell component="th" scope="row">
                                                {row.name}
                                            </TableCell>
                                            <TableCell component="th" scope="row">
                                                {fDateTime(row.startDate)}
                                            </TableCell>
                                            <TableCell component="th" scope="row">
                                                {fDateTime(row.endDate)}
                                            </TableCell>
                                            <TableCell component="th" scope="row">
                                                {row.fixturesCount}
                                            </TableCell>
                                            <TableCell component="th" scope="row">
                                                {fCoins(row.lineupCount)}
                                            </TableCell>
                                            <TableCell component="th" scope="row">
                                                {row.isCompleted ? (
                                                    <Iconify
                                                        icon={"eva:checkmark-circle-2-outline"}
                                                        sx={{ color: "green" }}
                                                    />
                                                ) : (
                                                    <Iconify
                                                        icon={"eva:close-circle-outline"}
                                                        sx={{ color: "red" }}
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell component="th" scope="row">
                                                {row.isPrizesPaid ? (
                                                    <Iconify
                                                        icon={"eva:checkmark-circle-2-outline"}
                                                        sx={{ color: "green" }}
                                                    />
                                                ) : (
                                                    <Iconify
                                                        icon={"eva:close-circle-outline"}
                                                        sx={{ color: "red" }}
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell component="th" scope="row">
                                                {getStageStatus(row)}
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton
                                                    size="large"
                                                    color="inherit"
                                                    onClick={handleOpenMenu}
                                                    id={row.stageId}
                                                >
                                                    <Iconify icon={"eva:more-vertical-fill"} />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Scrollbar>
                )}
            </Card>

            <Popover
                open={Boolean(open)}
                anchorEl={open}
                onClose={handleCloseMenu}
                anchorOrigin={{ vertical: "top", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{
                    sx: {
                        p: 1,
                        width: 160,
                        "& .MuiMenuItem-root": {
                            px: 1,
                            typography: "body2",
                            borderRadius: 0.75,
                        },
                    },
                }}
            >
                {!isViewer && (
                    <MenuItem onClick={() => handleMenuAction("edit", openId)}>
                        <Iconify icon={"eva:edit-outline"} sx={{ mr: 2 }} />
                        Edit
                    </MenuItem>
                )}

                <MenuItem onClick={() => handleMenuAction("leaderboard", openId)}>
                    <Iconify icon={"eva:list-outline"} sx={{ mr: 2 }} />
                    Leaderboard
                </MenuItem>
            </Popover>
        </>
    );
};

export default StagesTable;